import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { Router, RouterModule } from '@angular/router';
import { LandingComponent } from './pages/landing/landing.component';
import { MainBannerComponent } from './components/main-banner/main-banner.component';
import {
  BrowserAnimationsModule,
  NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from './modules/shared/shared.module';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { HttpMainInterceptor } from './services/http.interceptor';
import { AuthButtonComponent } from './components/auth-button/auth-button.component';
import { CategoryManagementComponent } from './components/category-management/category-management.component';
import { CreateCategoryComponent } from './components/category-management/create-category/create-category.component';
import { CategoryManagementSingleComponent } from './components/category-management/category-management-single/category-management-single.component';
import { OrganizationManagementComponent } from './components/organization-management/organization-management.component';
import { OrganizationManagementSingleComponent } from './components/organization-management/organization-management-single/organization-management-single.component';
import { CreateOrganizationComponent } from './components/organization-management/create-organization/create-organization.component';
import { CreateEventComponent } from './components/event-management/create-event/create-event.component';
import { EventManagementComponent } from './components/event-management/event-management.component';
import { EventManagementSingleComponent } from './components/event-management/event-management-single/event-management-single.component';
import { BrowseCategoriesComponent } from './pages/browse-categories/browse-categories.component';
import { BrowseOrganizationsComponent } from './components/browse-organizations/browse-organizations.component';
import { EventSinglePageComponent } from './pages/event-single-page/event-single-page.component';
import { KlixBannerComponent } from './components/klix-banner/klix-banner.component';
import { QuillModule } from 'ngx-quill';
import { EventCarouselFlatComponent } from './components/event-carousel-flat/event-carousel-flat.component';
import { MyCalendarComponent } from './pages/my-calendar/my-calendar.component';
import { MyCalendarShowOptionsComponent } from './components/my-calendar-show-options/my-calendar-show-options.component';
import { MyCalendarSyncComponent } from './components/my-calendar-sync/my-calendar-sync.component';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { MyCalendarEventsComponent } from './components/my-calendar-events/my-calendar-events.component';
import { MySettingsComponent } from './pages/my-settings/my-settings.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { LoaderComponent } from './components/loader/loader.component';
import { TranslateService } from './services/translate.service';
import { SITE_LANGUAGE } from './constants/constants';
import { EventManagementEventRowComponent } from './components/event-management/event-management-event-row/event-management-event-row.component';
import { EventManagementBasicDetailsComponent } from './components/event-management/event-management-basic-details/event-management-basic-details.component';
import { EventManagementBasicInfoComponent } from './components/event-management/event-management-basic-details/event-management-basic-info/event-management-basic-info.component';
import { EventManagementLocationComponent } from './components/event-management/event-management-basic-details/event-management-location/event-management-location.component';
import { EventManagementSportsAndDisciplinesComponent } from './components/event-management/event-management-basic-details/event-management-sports-and-disciplines/event-management-sports-and-disciplines.component';
import { InputRowComponent } from './components/inputs/input-row/input-row.component';
import { EventManagementSectionHeadingComponent } from './components/event-management/event-management-section-heading/event-management-section-heading.component';
import { EventManagementRegistrationDetailsComponent } from './components/event-management/event-management-registration-details/event-management-registration-details.component';
import { CmsModule } from './components/cms/cms.module';
import { CreateEventLegacyComponent } from './components/event-management/create-event/create-event-legacy.component';
import { ContactUsComponent } from './pages/contact-us/contact-us.component';
import { FacebookPixelComponent } from './facebook-pixel/facebook-pixel.component';
import { CookiesConsentComponent } from './components/cookies-consent/cookies-consent.component';
import * as Sentry from '@sentry/angular';

function setupTranslateServiceFactory(service: TranslateService): Function {
  return () => service.use(localStorage.getItem('lang') ?? SITE_LANGUAGE.EN);
}

@NgModule({
  declarations: [
    AppComponent,
    LandingComponent,
    MainBannerComponent,
    CategoryManagementComponent,
    CategoryManagementSingleComponent,
    CreateCategoryComponent,
    OrganizationManagementComponent,
    OrganizationManagementSingleComponent,
    CreateOrganizationComponent,
    CreateEventLegacyComponent,
    CreateEventComponent,
    EventManagementEventRowComponent,
    EventManagementComponent,
    EventManagementSingleComponent,
    BrowseCategoriesComponent,
    BrowseOrganizationsComponent,
    EventSinglePageComponent,
    EventCarouselFlatComponent,
    MyCalendarComponent,
    MyCalendarShowOptionsComponent,
    MyCalendarSyncComponent,
    MyCalendarEventsComponent,
    MySettingsComponent,
    LoaderComponent,
    EventManagementBasicDetailsComponent,
    EventManagementBasicInfoComponent,
    EventManagementLocationComponent,
    EventManagementSportsAndDisciplinesComponent,
    InputRowComponent,
    EventManagementSectionHeadingComponent,
    EventManagementRegistrationDetailsComponent,
    ContactUsComponent,
    FacebookPixelComponent,
    CookiesConsentComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    RouterModule,
    NoopAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    SharedModule,
    ImageCropperModule,
    CmsModule,
    SimpleNotificationsModule.forRoot(),
    QuillModule.forRoot(),
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    { provide: HTTP_INTERCEPTORS, useClass: HttpMainInterceptor, multi: true },
    TranslateService,
    {
      provide: APP_INITIALIZER,
      useFactory: setupTranslateServiceFactory,
      deps: [TranslateService, Sentry.TraceService],
      multi: true,
    },
  ],
  exports: [AuthButtonComponent, EventSinglePageComponent, KlixBannerComponent],
  bootstrap: [AppComponent],
})
export class AppModule {}
